import React from 'react';

// Utils
import { handleLaunchExternalLink } from 'Shared/utils/external-link';

// Hooks
import { useWorkspace } from 'Shared/utils/hooks/use-workspace';
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';
import { useCreateOrSendBa } from 'Shared/utils/hooks/use-create-or-send-ba';
import { useBaProfile } from 'Shared/utils/hooks/use-ba-profile';
import { useBaTracking } from 'Shared/tracking/use-ba-tracking';

// Components
import { WpButton, WpButtonGroup } from 'Wpds/components/button';
import PiSuggestedTopicsEmptyState from './PiSuggestedTopicsEmptyState';

// Styles
import './PiSuggestedTopicsMissingBAState.scss';

type Props = {
  categoryDropdown: React.ReactNode;
};

const PiSuggestedTopicsMissingBAState = ({ categoryDropdown }: Props) => {
  const { otherUserInOneOnOne } = useWorkspace();
  const { user } = useCurrentUser();
  const { trackTakeAssessmentButtonClick, trackSendAssessmentButtonClick } = useBaTracking({
    component: 'PI Suggested Topics Missing BA Component',
  });

  const { getBaUrl, isCreatingBaLink: isCreatingBaLinkForSelf } = useCreateOrSendBa({
    user: user,
    isTakingBaForSelf: true,
  });

  const { sendBaEmail, resendBaEmail, isCreatingBaLink } = useCreateOrSendBa({
    user: otherUserInOneOnOne,
    isTakingBaForSelf: false,
  });

  const {
    referencePatternName: cuReferencePatternName,
    hasPendingAssesmentId: cuHasPendingAssesmentId,
    hasPendingBa: cuHasPendingBa,
  } = useBaProfile({ user, withFetch: false });
  const {
    referencePatternName: ouReferencePatternName,
    hasPendingAssesmentId: ouHasPendingAssesmentId,
    hasPendingBa: ouHasPendingBa,
  } = useBaProfile({ user: otherUserInOneOnOne, withFetch: false });

  const handleTakeAssessmentClick = async () => {
    const url = await getBaUrl();
    trackTakeAssessmentButtonClick();
    handleLaunchExternalLink({ href: url });
  };

  const handleSendAssessmentClick = async () => {
    await sendBaEmail();
    trackSendAssessmentButtonClick();
  };

  const handleResendAssessmentClick = async () => {
    await resendBaEmail();
  };

  const baseClassName = 'PiSuggestedTopicsMissingBAState';

  return (
    <div className={baseClassName}>
      <div className={`${baseClassName}__toolbar`}>{categoryDropdown}</div>
      <PiSuggestedTopicsEmptyState
        title="Get PI-suggested topics"
        description={`Once you and ${otherUserInOneOnOne.firstName} both complete the PI Behavioral Assessment, you'll get curated, custom agenda topics designed to strengthen your relationship.`}
        actions={
          <WpButtonGroup>
            {!cuReferencePatternName && (
              <WpButton
                variant="secondary"
                onClick={handleTakeAssessmentClick}
                disabled={cuHasPendingAssesmentId || cuHasPendingBa}
                loading={isCreatingBaLinkForSelf}
              >
                {cuHasPendingAssesmentId || cuHasPendingBa ? 'Assessment pending...' : 'Take Assessment'}
              </WpButton>
            )}
            {!ouReferencePatternName && !ouHasPendingAssesmentId && !ouHasPendingBa && (
              <WpButton variant="secondary" onClick={handleSendAssessmentClick} loading={isCreatingBaLink}>
                Send assessment to {otherUserInOneOnOne.firstName}
              </WpButton>
            )}
            {!ouReferencePatternName && (ouHasPendingAssesmentId || ouHasPendingBa) && (
              <WpButton variant="secondary" onClick={handleResendAssessmentClick} loading={isCreatingBaLink}>
                Resend assessment to {otherUserInOneOnOne.firstName}
              </WpButton>
            )}
          </WpButtonGroup>
        }
      />
    </div>
  );
};

export default PiSuggestedTopicsMissingBAState;
